import { useState, useRef, MouseEvent as ReactMouseEvent } from 'react';
import { throttle } from 'lodash';
import clsx from 'clsx';

import { Svgs } from 'environment';
import { CollaboratorPermissions } from 'store/data/collaborators';
import { EventType, LanguageType } from 'types/index';
import { ProjectCollaboratorAvatars } from '../ProjectCollaboratorAvatars';
import { Option } from '../ProjectsTable';

import { Dropdown } from 'components/UI/Dropdown';
import { Icon } from 'components/UI/Icons';

import { stringAsBoolean, loseFocus } from 'helpers/generic';
import { computeProjectTitle } from 'helpers/projects';

import { useProjectById, useForbidPersonalData } from 'hooks/store';
import { useOutsideClick, useEffectOnce, useSelector } from 'hooks/utils';

const IS_PROJECT_SHARED_AVATARS_ENABLED = stringAsBoolean(
	process.env.REACT_APP_PROJECT_SHARED_AVATARS!
);

export interface ProjectCardOptions {
	onClick: (projectId: string, userAcces?: CollaboratorPermissions) => void;
	onReadMore: (description: string) => void;
}

interface Props extends ProjectCardOptions {
	projectId: string;
	projectOptions: (projectOwner?: string, userAcces?: CollaboratorPermissions) => Option[];
	isFrozen?: boolean;
}

export function ProjectCard({
	projectId,
	onClick,
	onReadMore,
	projectOptions,
	isFrozen = false
}: Props) {
	const language = useSelector(state => state.ui.i18n.language);
	const dictionary = DICTIONARY[language];

	const [menuVisible, setMenuVisible] = useState(false);
	const elementValueRef = useRef<HTMLAnchorElement>(null);

	useOutsideClick(() => setMenuVisible(false), [elementValueRef]);

	const project = useProjectById(projectId);
	const { forbidPersonalData } = useForbidPersonalData(projectId ?? '');

	if (!project) return null;

	function onReadMoreClick(e: ReactMouseEvent) {
		stopEvent(e);
		closeMoreMenu();
		if (project) onReadMore(project.description);
	}

	function closeMoreMenu() {
		setMenuVisible(false);
		loseFocus();
	}

	function stopEvent(e: ReactMouseEvent) {
		e.preventDefault();
		e.stopPropagation();
	}

	const titleContent = project ? computeProjectTitle(project) : '';

	return (
		<a
			ref={elementValueRef}
			onClick={() => {
				if (isFrozen) {
					// See ProjectCard for why it's implemented this way :')
					const options = projectOptions(project.createdByUser, project.userAccess);
					const editTitle = dictionary.edit;
					const viewTitle = dictionary.view;

					const editOrViewOption = options.find(
						option => option.title === editTitle || option.title === viewTitle
					);

					if (editOrViewOption) {
						editOrViewOption.onClick(projectId);
					}
					return;
				}
				onClick(projectId, project.userAccess);
			}}
			data-test-id={project.projectName.replaceAll(/[^a-zA-Z]+/g, '').toLowerCase()}
			id={project.projectName.replaceAll(/[^a-zA-Z]+/g, '').toLowerCase()}
			data-testid={'project-card_' + project.projectName}
			className={clsx(
				'group/card relative flex flex-col w-full h-[22.8rem] p-6 pt-8 rounded-md no-underline transition-all duration-200',
				isFrozen
					? 'bg-gray-100 shadow-normal cursor-pointer'
					: 'cursor-pointer bg-white hover:bg-primary-500 shadow-normal hover:shadow-[0_0.6rem_1.2rem_0.6rem_rgba(37,37,58,0.15)]'
			)}
		>
			{forbidPersonalData && (
				<div className="absolute top-0 left-2 bg-gray-100 p-1">
					{' '}
					<Icon svg={Svgs.Pdna} size={s => s.s} />
				</div>
			)}
			<div className="flex justify-between mb-4">
				<span
					title={project.lastModifiedDate}
					className={clsx('text-sm text-gray-700 group-hover/card:text-white')}
				>
					{project.lastModifiedDate}
				</span>
				{!isFrozen && (
					<div className={clsx('absolute top-2 right-2')}>
						<Dropdown
							toggleComponent={({ ref, toggle }) => (
								<Icon
									ref={ref}
									variant={v => v.button}
									svg={Svgs.More}
									onClick={e => {
										stopEvent(e);
										loseFocus();
										setMenuVisible(state => !state);
										toggle();
									}}
									active={menuVisible}
									dataTestId={
										project
											? 'project-settings-dropdown_' + project.projectName
											: undefined
									}
								/>
							)}
							width={18}
							offset={{ top: 20, right: 20 }}
						>
							{projectOptions(project.createdByUser, project.userAccess).map(
								(option, index) => (
									<Dropdown.Item
										key={index}
										title={option.title}
										onClick={() => {
											closeMoreMenu();
											option.onClick(projectId);
										}}
										dataTestId={
											'project-settings-dropdown-item_' + option.title
										}
									/>
								)
							)}
						</Dropdown>
					</div>
				)}
			</div>
			<div className="flex flex-col w-full h-full">
				<h1
					title={titleContent}
					className={clsx(
						'text-base font-bold mb-2 line-clamp-3',
						!isFrozen && 'text-black group-hover/card:text-white'
					)}
				>
					{titleContent}
				</h1>
				{isFrozen && (
					<p className="text-xs text-yellow-600 mt-1">{dictionary.frozenMessage}</p>
				)}
				<ProjectDescriptionComponent
					description={project.description}
					onReadMoreClick={onReadMoreClick}
					language={language}
				/>
			</div>
			<div className={clsx('flex items-end justify-between min-h-6 mt-auto')}>
				{IS_PROJECT_SHARED_AVATARS_ENABLED && (
					<ProjectCollaboratorAvatars projectId={projectId} />
				)}
			</div>
		</a>
	);
}

interface ProjectDescriptionComponentProps {
	description: string;
	onReadMoreClick: (event: ReactMouseEvent<HTMLSpanElement>) => void;
	language: LanguageType;
}

function ProjectDescriptionComponent({
	description,
	onReadMoreClick,
	language
}: ProjectDescriptionComponentProps) {
	const descriptionRef = useRef<HTMLDivElement>(null);
	const [showReadMore, setShowReadMore] = useState(false);

	useEffectOnce(() => {
		handleReadMore();
		const debouncedHandleReadMore = throttle(handleReadMore, 100);
		window.addEventListener(EventType.Resize, debouncedHandleReadMore, true);
		return () => window.removeEventListener(EventType.Resize, debouncedHandleReadMore, true);
	});

	function handleReadMore() {
		if (!descriptionRef.current) return;
		const { clientHeight, scrollHeight } = descriptionRef.current;
		const ellipsisExists = clientHeight !== scrollHeight;
		setShowReadMore(ellipsisExists);
	}

	return (
		<>
			<div
				ref={descriptionRef}
				title={description}
				className={clsx('text-sm line-clamp-3 text-gray-700 group-hover/card:text-white')}
			>
				{description}
			</div>

			{showReadMore && (
				<div className="mt-1">
					<button
						onClick={onReadMoreClick}
						className={clsx(
							'text-sm font-medium cursor-pointer text-gray-700 group-hover/card:text-white'
						)}
					>
						{DICTIONARY[language].readMore}
					</button>
				</div>
			)}
		</>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		frozenMessage: 'This project is frozen, please contact your administrator to unfreeze it.',
		edit: 'Edit project',
		view: 'View project',
		readMore: 'Read more'
	},
	[LanguageType.Norwegian]: {
		frozenMessage:
			'Dette prosjektet er fryst, vennligst kontakt administratoren for å løse det.',
		edit: 'Rediger prosjektet',
		view: 'Se prosjektet',
		readMore: 'Les mer'
	}
};
